import { MouseEvent } from 'react';

import {
  Box,
  IconButton,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import ListAltIcon from '@material-ui/icons/ListAlt';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useConfirm } from 'material-ui-confirm';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import ModalEditarAgendamento from 'components/ModalEditarAgendamento';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch } from 'store';
import {
  AgendamentoInterfaceV2,
  AgendamentoStatusEnum,
  cancelarAgendamento,
  fetchAgendamentosV2,
  resetAgendamentosV2,
  resetIdsAgendamentosSelecionados,
} from 'store/reducers/agendamentos';
import { ContatoInterface } from 'store/reducers/contatos';

import ModalDetalhesAgendamento from './ModalDetalhesAgendamento';

interface AgendamentoMenuAcoesProps {
  agendamento: AgendamentoInterfaceV2;
  onSuccess?: () => void;
  onToggleSelection: () => void;
}

const useStyles = makeStyles(({ typography }) => ({
  paper: {
    '& .MuiDialogTitle-root .MuiTypography-h6': {
      fontSize: typography.pxToRem(14),
      fontWeight: typography.fontWeightMedium as number,
    },
    '& .MuiDialogContent-root .MuiDialogContentText-root': {
      fontSize: typography.pxToRem(14),
    },
  },
}));

export default function AgendamentoMenuAcoes({
  agendamento,
  onSuccess,
  onToggleSelection,
}: AgendamentoMenuAcoesProps) {
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const currentUser = useCurrentUser();
  const classes = useStyles();

  const [showModalEditarAgendamento, hideModalEditarAgendamento] = useModal(
    ({ in: open }) => (
      <ModalEditarAgendamento
        open={open}
        onClose={hideModalEditarAgendamento}
        agendamento={agendamento}
        contato={
          {
            ...agendamento.contact,
            nome: agendamento.contact.name,
            contato: agendamento.contact.number,
            foto: agendamento.contact.photoUrl || '',
          } as unknown as ContatoInterface
        }
        onSuccess={onSuccess}
      />
    ),
    [agendamento],
  );

  const [showModalDetalhesAgendamento, hideModalDetalhesAgendamento] = useModal(
    ({ in: open }) => (
      <ModalDetalhesAgendamento
        open={open}
        onClose={hideModalDetalhesAgendamento}
        agendamento={agendamento}
        onSuccess={onSuccess}
      />
    ),
    [agendamento],
  );

  const handleCancel = (
    e: MouseEvent<HTMLLIElement>,
    agendamentoId: string,
  ) => {
    e.stopPropagation();

    confirm({
      title: 'Cancelar agendamento',
      description: 'Deseja realmente cancelar este agendamento?',
      cancellationText: 'Não',
      confirmationText: 'Sim',
      dialogProps: { maxWidth: 'xs', classes: { paper: classes.paper } },
    })
      .then(async () => {
        await dispatch(cancelarAgendamento(agendamentoId))
          .unwrap()
          .then(() => {
            enqueueSnackbar(
              'O agendamento foi cancelado e não está mais disponível para você.',
            );

            if (currentUser) {
              dispatch(resetAgendamentosV2());
              dispatch(
                fetchAgendamentosV2({
                  attendantId: currentUser.id,
                }),
              );
            }

            dispatch(resetIdsAgendamentosSelecionados());
            onSuccess?.();
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      })
      .catch(() => undefined);
  };

  return (
    <PopupState variant="popover" popupId={`contato-menu-${agendamento.id}`}>
      {popupState => (
        <Box onClick={e => e.stopPropagation()}>
          <IconButton
            size="small"
            {...bindTrigger(popupState)}
            onClick={event => {
              onToggleSelection();
              bindTrigger(popupState).onClick(event);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu {...bindMenu(popupState)}>
            <MenuItem
              onClick={() => {
                showModalDetalhesAgendamento();
                popupState.close();
              }}
            >
              <ListItemIcon style={{ minWidth: 28 }}>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Detalhes</Typography>
            </MenuItem>
            {agendamento.status !== AgendamentoStatusEnum.CANCELADO && (
              <Box>
                <MenuItem
                  onClick={() => {
                    showModalEditarAgendamento();
                    popupState.close();
                  }}
                >
                  <ListItemIcon style={{ minWidth: 28 }}>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Editar</Typography>
                </MenuItem>
                <MenuItem
                  onClick={e => {
                    handleCancel(e, agendamento.id);
                    popupState.close();
                  }}
                >
                  <ListItemIcon style={{ minWidth: 28 }}>
                    <CancelIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <Typography color="error" variant="body2">
                    Cancelar
                  </Typography>
                </MenuItem>
              </Box>
            )}
          </Menu>
        </Box>
      )}
    </PopupState>
  );
}
